import { Alert, Card, Col, Row, Skeleton, Statistic, Table, Tag, Typography } from 'antd';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { errorMessage } from '../../api/client';
import { useDealerAccountDashboard } from '../../api/hooks';
import type { DealerLedgerEntry } from '../../types';
import { date, money } from '../../utils/format';

const TYPE_COLOR: Record<string, string> = {
  Placement: 'blue',
  PrincipalReturn: 'orange',
  ProfitReceipt: 'green',
};

/** The ledger stores Placement/PrincipalReturn/ProfitReceipt; every screen says this instead. */
const TYPE_LABEL: Record<string, string> = {
  Placement: 'Investment',
  PrincipalReturn: 'Withdrawal',
  ProfitReceipt: 'Profit payment',
};

/**
 * The dealer's own summary — the same three panels the investor portal shows: profit received,
 * capital given vs received, and the transaction history. Every figure comes from
 * /dealers/{id}/dashboard, which is scoped server-side; no other dealer's data is reachable.
 */
export default function DealerMyDashboard({ dealerId }: { dealerId: number }) {
  const { data, isLoading, error } = useDealerAccountDashboard(dealerId);

  if (error) return <Alert type="error" showIcon message={errorMessage(error)} />;
  if (isLoading || !data) return <Skeleton active paragraph={{ rows: 6 }} />;

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Current capital"
              value={money(data.currentPrincipal)}
              prefix="PKR"
              valueStyle={{ fontSize: 20, color: '#1677ff' }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Invested {money(data.totalGiven)} · returned {money(data.totalReturned)}
            </Typography.Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Profit outstanding"
              value={money(data.outstandingProfit)}
              prefix="PKR"
              valueStyle={{ fontSize: 20, color: data.outstandingProfit > 0 ? '#cf1322' : '#3f8600' }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Earned but not yet paid
            </Typography.Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Profit earned"
              value={money(data.totalAccruedProfit)}
              prefix="PKR"
              valueStyle={{ fontSize: 20 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Profit received"
              value={money(data.totalReceivedProfit)}
              prefix="PKR"
              valueStyle={{ fontSize: 20, color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Profit received — last 12 months" size="small" style={{ marginTop: 16 }}>
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <BarChart data={data.monthlyReceivedProfit} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tickLine={false} fontSize={12} />
              <YAxis tickFormatter={(v: number) => money(v)} width={80} tickLine={false} fontSize={12} />
              <Tooltip formatter={(v: number) => `PKR ${money(v)}`} />
              <Bar dataKey="amount" fill="#52c41a" radius={[4, 4, 0, 0]} name="Profit received" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="Capital given vs received — last 12 months" size="small" style={{ marginTop: 16 }}>
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <BarChart data={data.monthlyCapitalFlow} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tickLine={false} fontSize={12} />
              <YAxis tickFormatter={(v: number) => money(v)} width={80} tickLine={false} fontSize={12} />
              <Tooltip formatter={(v: number) => `PKR ${money(v)}`} />
              <Legend />
              <Bar dataKey="given" fill="#1677ff" radius={[4, 4, 0, 0]} name="Capital given" />
              <Bar dataKey="received" fill="#fa8c16" radius={[4, 4, 0, 0]} name="Capital received" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="Recent transactions" size="small" style={{ marginTop: 16 }}>
        <Table<DealerLedgerEntry>
          dataSource={data.recentTransactions}
          rowKey={(row, index) => `${row.type}-${row.date}-${index}`}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No transactions yet' }}
          columns={[
            { title: 'Date', dataIndex: 'date', render: date },
            {
              title: 'Type',
              dataIndex: 'type',
              render: (value: string) => <Tag color={TYPE_COLOR[value] ?? 'default'}>{TYPE_LABEL[value] ?? value}</Tag>,
            },
            { title: 'Description', dataIndex: 'description' },
            { title: 'Given', dataIndex: 'given', align: 'right', render: (v: number) => (v ? money(v) : '—') },
            { title: 'Received', dataIndex: 'received', align: 'right', render: (v: number) => (v ? money(v) : '—') },
          ]}
        />
      </Card>
    </>
  );
}
